<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="users" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Casual Management</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            v-if="permissions.includes('create casuals')"
            class="btn btn-primary shadow-sm"
            :class="subscription.package.casuals <= casualData.length ? 'disabled' : ''"
            @click="addCasual"
          >
            <vue-feather type="user-plus" size="18" class="me-1"></vue-feather>
            Add Casual
          </button>
          <button
            v-if="profile.role === 'primary'"
            class="btn btn-outline-primary shadow-sm ms-3"
            @click="$refs.refInputEl.click()"
          >
            <vue-feather type="upload" size="18" class="me-1"></vue-feather>
            Upload Casuals
          </button>
        </div>
      </div>
    </div>

    <!-- Subscription Alert -->
    <div v-if="subscription.package.casuals <= casualData.length" role="alert" class="alert alert-info shadow-sm mb-4">
      <div class="d-flex align-items-center">
        <vue-feather type="alert-circle" size="18" class="me-2"></vue-feather>
        <div>
          Your subscription has reached the limit of {{ subscription.package.casuals }} casuals.
          <button class="btn btn-sm btn-dark text-uppercase ms-3" @click="$router.push('upgrade')">Upgrade</button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Casuals ({{casualData.length}})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search casual" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Hidden file input -->
          <input
            ref="refInputEl"
            type="file"
            name="logo"
            accept=".xlsx,.xls"
            hidden
            @input="uploadCasualFile"
          >

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Casual Table -->
          <div v-if="!loading && casualData.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">#</th>
                  <th scope="col" class="fw-semibold">Casual No</th>
                  <th scope="col" class="fw-semibold">Name</th>
                  <th scope="col" class="fw-semibold">ID Number</th>
                  <th scope="col" class="fw-semibold">Phone</th>
                  <th scope="col" class="fw-semibold">Status</th>
                  <th scope="col" class="fw-semibold">Created</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in casualData" :key="index" class="casual-row">
                  <td>{{ index + 1 }}</td>
                  <td>{{ row[1] }}</td>
                  <td class="fw-medium">{{ row[2] }}</td>
                  <td>{{ row[3] }}</td>
                  <td>{{ row[4] }}</td>
                  <td>
                    <span class="badge" :class="'bg-' + getPillByStatus(row[5])">{{ row[5] }}</span>
                  </td>
                  <td>{{ row[6] }}</td>
                  <td class="text-end">
                    <button
                      v-if="permissions.includes('view casuals')"
                      class="btn btn-sm btn-outline-primary"
                      @click="casualDetails(row[0])"
                    >
                      <vue-feather type="eye" size="16"></vue-feather>
                      Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && casualData.length === 0 && permissions.includes('create casuals')" class="text-center py-4">
            <div class="empty-state">
              <div class="bg-light p-4 rounded-circle mb-3 d-inline-block">
                <vue-feather type="users" size="32" stroke="#6c757d"></vue-feather>
              </div>
              <h5>No Casuals Found</h5>
              <p class="text-muted">Start by adding your first casual worker</p>
              <button
                v-if="profile.role !== 'user'"
                class="btn btn-primary"
                :class="subscription.package.casuals <= 0 ? 'disabled' : ''"
                @click="addCasual"
              >
                <vue-feather type="user-plus" size="16" class="me-1"></vue-feather>
                Add Casual
              </button>
              <button
                v-if="profile.role === 'primary'"
                class="btn btn-outline-primary ms-3"
                @click="$refs.refInputEl.click()"
              >
                <vue-feather type="upload" size="16" class="me-1"></vue-feather>
                Upload Casuals
              </button>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="!loading && casualData.length > 0" class="pagination-container d-flex flex-wrap justify-content-around pt-3 pb-4">
            <div class="col-md-3">
              <button class="btn btn-outline-dark text-uppercase shadow-sm" @click="$router.go(-1)">
                <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
                Back
              </button>
            </div>
            <div class="col-md-6 text-center">
              <p class="mb-2 small">Rows per page</p>
              <select class="form-select shadow-sm mx-auto rows-per-page" v-model="dataTablePerPage" data="">
                <option v-for="item in [5, 10, 25, 50, 100]" :key="item" :value="item">{{item}}</option>
              </select>
            </div>
            <div class="col-md-3">
              <Pagination v-model="dataTablePage" :pages="Math.ceil(dataTable.length/dataTablePerPage)">
                <template v-slot:next>
                  Next
                </template>
                <template v-slot:previous>
                  Prev
                </template>
              </Pagination>
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import casualService from "@/api/casualService";
import formatDate from "@/utils/formatDate";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import VueFeather from 'vue-feather';

export default {
  name: "Casuals",
  components: { VueFeather },

  data(){
    return {
      profile: {},
      loading: true,
      permissions: [],
      dataTableHeaders: [
        '#',
        'Casual No',
        'Name',
        'ID Number',
        'Phone',
        'Status',
        'Created',
        null
      ],
      dataTable: [],
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
      subscription: {
        package: {}
      },
    }
  },

  methods:{
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'inactive':
          return 'danger';
        case 'active':
          return 'success';
        case 'suspended':
          return 'warning';
        case 'terminated':
          return 'dark';
      }
    },
    async getCasuals() {
      this.loading = true
      try {
        this.dataTable = await casualService.getCasuals();
      } catch (error) {
        console.error("Error fetching casuals:", error)
      } finally {
        this.loading = false
      }
    },
    casualDetails(id) {
      router.push('/casual/' + id)
    },
    addCasual(){
      router.push('/add-casual')
    },
    uploadCasualFile : async function(file) {
      const fileReader = new FileReader()
      const { files } = file.target
      let response = false
      if (files && files.length) {
        fileReader.readAsDataURL(files[0])
        fileReader.onload = async () => {
          if (typeof fileReader.result === 'string') {
            try {
              response = await casualService.uploadCasualFile({ file: fileReader.result })
              if (response) {
                await this.getCasuals()
              }
            } catch (error) {
              console.error("Error uploading casual file:", error)
            }
          }
        }
      }
    }
  },

  computed: {
    casualData(){
      let data = [];
      for (let i = 0; i < this.dataTable.length; i++) {
        let fields = [
          this.dataTable[i].id,
          this.dataTable[i].casual_no,
          this.dataTable[i].first_name + ' ' + this.dataTable[i].last_name,
          this.dataTable[i].id_number,
          this.dataTable[i].phone,
          this.dataTable[i].status,
          formatDate(this.dataTable[i].created_at)
        ];
        data.push(fields);
      }
      return data;
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.permissions = this.profile.permissionNames
    await this.getCasuals()
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

}
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.text-primary {
  color: #652aa3 !important;
}

.casual-row {
  transition: all 0.2s ease;
}

.casual-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.empty-state {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.font-size-125 {
  font-size: 1.25rem;
}

.color-gray-600 {
  color: #6c757d;
}

.search-input {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.search-input:focus-within {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
}

.btn {
  transition: all 0.2s ease;
}

.btn:hover:not(.disabled) {
  transform: translateY(-2px);
}

.badge {
  padding: 0.5em 0.75em;
}

.rows-per-page {
  width: 80px;
  margin: 0 auto;
}
</style>
